import { Column } from '@/src/components/common/Column';
import FileDragAndDrop from '@/src/components/common/FileDragAndDrop';
import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import { TranslatedAttribute } from '@/src/types/common';
import { downloadAs } from '@/src/utils/download.utils';
import { LANGUAGE_SELECT_OPTIONS } from '@/src/utils/locale.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import {
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  Header,
  Select,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageCodes } from '../../../../constants/shared/language-codes';
import { useApi } from '../../../../store/api.context';
import { ChallengePropAction, useCreateChallenge } from '../../../../store/create-challenge.context';
import { Challenge, TranslatedChallengeWrapper } from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import './index.scss';
import ChallengeTranslationDetail from './Translation';
import {
  convertTranslatedAttributesToChallenge,
  exportChallengeForTranslation,
  parseTranslationFileToFormData,
} from '@/src/components/challenges/challengeDetailSections/challengeTranslation/ChallengeTranslationDetails.utils';
import { useChallenges } from '@/src/store/challenge.context';
import { useParams } from 'react-router-dom';
import { sortBy } from 'lodash';

interface ChallengeTranslationDetailsProps {
  challenge: Challenge;
}

const ChallengeTranslationDetails: React.FC<ChallengeTranslationDetailsProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const params: { 0: string } = useParams();
  const { editMode, toggleChallengeEditMode, handleUpdateChallengeProp } = useCreateChallenge();
  const { getChallenge } = useChallenges();
  const [langCode, setLangCode] = useState('');
  const { challengesApi } = useApi();
  const [translatedChallenge, setTranslatedChallenge] = useState<TranslatedChallengeWrapper>();
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<OptionDefinition | null>(null);
  const [formData, setFormData] = useState<TranslatedAttribute[]>([]);
  const [usingLatest, setUsingLatest] = useState(false);
  const timeoutId = useRef(0);

  const getData = async (languageCode: string) => {
    if (!!challenge?.id) {
      setLoading(true);
      try {
        const res = await challengesApi.getTranslatedChallenge(challenge.id, languageCode, usingLatest);
        setTranslatedChallenge(res);
      } catch (err) {
        preProdLogger('Error fetching translated challenge', err);
      }
    } else {
      preProdLogger('Missing Challenge ID to fetch translations');
    }
    setLoading(false);
  };

  const formUpdate = ({ attributeName, originalText, translatedText }: TranslatedAttribute) => {
    let tempData = [...formData];
    if (tempData.length > 0) {
      const found = tempData.some((obj, index) => {
        if (obj.attributeName === attributeName) {
          tempData[index] = { attributeName, originalText, translatedText };
          return true;
        }
      });
      if (!found) {
        tempData = [...tempData, { attributeName, originalText, translatedText }];
      }
    } else {
      tempData = [...tempData, { attributeName, originalText, translatedText }];
    }
    setFormData(tempData);
  };

  const withoutEnglish: { label: string; value: string }[] = LANGUAGE_SELECT_OPTIONS.filter(
    (i) => i.value !== LanguageCodes.map.ENGLISH
  ).map((languageCode) => {
    return {
      label: languageCode.label,
      value: languageCode.value,
    };
  });
  const onLanguageChange = (language: OptionDefinition) => {
    setLangCode(language.value || '');
    if (!editMode) toggleChallengeEditMode(challenge); // enable edit mode only once
    void getData(language.value || '');
    setSelectedLanguage(language);
  };

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  useEffect(() => {
    // Nothing to stage until a language is chosen. Publishing on mount would
    // register a translation patch with an empty languageCode, which a later
    // save from any section would then replay against the backend.
    if (!langCode) {
      return;
    }
    handleUpdateChallengeProp(ChallengePropAction.TRANSLATION, {
      id: challenge.challengeId || '',
      languageCode: langCode,
      translatedAttributes: formData,
      latest: usingLatest,
    });
  }, [formData]);

  useEffect(() => {
    if (langCode) {
      getData(langCode);
    }
  }, [usingLatest]);

  useEffect(() => {
    // there's a bug where the challenge gets wiped out after submitting a change
    // this is more of a mitigation rather than a fix.
    if (!challenge?.challengeId && !!params[0]) {
      setLoading(true);
      getChallenge(params[0]).finally(() => setLoading(false));
    }
  }, [challenge?.challengeId]);

  const toVersionStr = (item?: Challenge | null) => {
    if (item?.majorVersion) {
      return `${item.majorVersion}.${item.minorVersion || 0}`;
    }
    return '';
  };

  const onClickExport = () => {
    downloadAs(
      exportChallengeForTranslation(translatedChallenge?.original),
      `translation-${challenge.challengeId}-${toVersionStr(challenge)}-en.md`,
      'text/html'
    );
  };

  const translatedTasks = useMemo(() => {
    if (!translatedChallenge?.translated?.props?.tasks?.length) {
      return [];
    }
    return sortBy(translatedChallenge?.translated?.props?.tasks, 'taskNumber');
  }, [translatedChallenge?.translated?.props?.tasks]);

  if (loading) {
    return <JamSpinner />;
  }
  return (
    <div className="challenge-translation">
      <SpaceBetween direction="vertical" size="m">
        {!translatedChallenge ? (
          <Container header={<Header variant="h2">{t(i18nKeys.challenges.subNavigation.translation)}</Header>}>
            <SpaceBetween size="xs">
              <strong>{t(i18nKeys.challenges.challengeDetails.titles.translation)}</strong>
              <div style={{ width: '300px' }}>
                <Select
                  data-testid="challenge-translation__language-select"
                  selectedOption={selectedLanguage}
                  placeholder={t(i18nKeys.settings.jamUILocalization.placeholder.selectLanguage)}
                  onChange={({ detail }) => onLanguageChange(detail.selectedOption)}
                  options={withoutEnglish}
                  filteringType="auto"
                />
              </div>
            </SpaceBetween>
          </Container>
        ) : (
          <>
            <Container header={<Header variant="h2">{t(i18nKeys.challenges.subNavigation.translation)}</Header>}>
              <Container>
                <Box padding={{ bottom: 'm' }}>
                  <Box float="left">
                    <strong>
                      {t(i18nKeys.challenges.challengeDetails.titles.translatingVersion)}{' '}
                      {toVersionStr(usingLatest ? challenge : translatedChallenge.original)}
                    </strong>
                  </Box>
                  <Box float="right">
                    <Toggle checked={usingLatest} onChange={() => setUsingLatest(!usingLatest)}>
                      {t(i18nKeys.challenges.challengeDetails.titles.useLatestVersion, {
                        version: toVersionStr(challenge),
                      })}
                    </Toggle>
                  </Box>
                </Box>
              </Container>

              <Box fontWeight="bold" padding={{ top: 'm', bottom: 's' }}>
                {t(i18nKeys.challenges.challengeDetails.titles.translation)}
              </Box>

              <ColumnLayout columns={2}>
                <Column size="s">
                  <SpaceBetween size="xs">
                    <div style={{ width: '300px' }}>
                      <Select
                        selectedOption={selectedLanguage}
                        onChange={({ detail }) => onLanguageChange(detail.selectedOption)}
                        options={withoutEnglish}
                        filteringType="auto"
                      />
                    </div>
                  </SpaceBetween>
                </Column>
                <Column size="s">
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="s">
                      <ButtonDropdown
                        items={[
                          {
                            text: selectedLanguage?.label || '',
                            id: selectedLanguage?.value || '',
                          },
                          {
                            text: t(i18nKeys.settings.jamUILocalization.labels.english) || '',
                            id: 'en',
                          },
                        ]}
                        data-testid="challenge-translation-export"
                        onItemClick={(e) =>
                          e.detail?.id === 'en'
                            ? onClickExport()
                            : downloadAs(
                                exportChallengeForTranslation(translatedChallenge?.translated),
                                `translation-${challenge.challengeId}-${toVersionStr(challenge)}-${
                                  selectedLanguage?.value
                                }.md`,
                                'text/html'
                              )
                        }
                      >
                        {t(i18nKeys.challenges.challengeDetails.buttons.download)}
                      </ButtonDropdown>
                      <Button
                        data-testid="challenge-translation-retranslate"
                        iconName="refresh"
                        onClick={() => void getData(langCode)}
                      >
                        {t(i18nKeys.challenges.challengeDetails.buttons.retranslate)}
                      </Button>
                    </SpaceBetween>
                  </Box>
                </Column>
              </ColumnLayout>
              <br />
              <FileDragAndDrop
                onUpdate={(files) =>
                  files[0].text().then((content) => {
                    if (!!translatedChallenge?.translated) {
                      const parsed = parseTranslationFileToFormData(challenge, content);
                      if (parsed.length) {
                        const clone = convertTranslatedAttributesToChallenge(parsed, translatedChallenge.translated);
                        setLoading(true);
                        timeoutId.current = setTimeout(() => {
                          setTranslatedChallenge(Object.assign(translatedChallenge, { translated: clone }));
                          setLoading(false);
                        }, 100);
                      }
                      setFormData(parsed);
                    }
                  })
                }
              />
            </Container>
            <ChallengeTranslationDetail
              attributeName="title"
              title={{
                label: t(i18nKeys.challenges.challengeDetails.titles.title),
                value: translatedChallenge?.translated?.props.title || '',
              }}
              originalTitle={{
                label: t(i18nKeys.challenges.challengeDetails.titles.title),
                value: translatedChallenge?.original?.props.title || '',
              }}
              formUpdate={({ attributeName, translatedText }) => {
                formUpdate({ attributeName, originalText: challenge?.props?.title || '', translatedText });
              }}
            />
            <ChallengeTranslationDetail
              attributeName="category"
              title={{
                label: t(i18nKeys.challenges.challengeDetails.titles.category),
                value: translatedChallenge?.translated?.props.category || '',
              }}
              originalTitle={{
                label: t(i18nKeys.challenges.challengeDetails.titles.category),
                value: translatedChallenge?.original?.props.category || '',
              }}
              formUpdate={({ attributeName, translatedText }) => {
                formUpdate({ attributeName, originalText: challenge?.props?.category || '', translatedText });
              }}
            />
            <ChallengeTranslationDetail
              attributeName="description"
              heading={t(i18nKeys.challenges.challengeDetails.titles.description)}
              originalDescription={{
                label: t(i18nKeys.challenges.challengeDetails.titles.description),
                value: translatedChallenge?.original?.props.description || '',
              }}
              description={{
                label: t(i18nKeys.challenges.challengeDetails.titles.description),
                value: translatedChallenge?.translated?.props.description || '',
              }}
              editor
              formUpdate={({ attributeName, translatedText }) => {
                formUpdate({ attributeName, originalText: challenge?.props?.description || '', translatedText });
              }}
            />
            {translatedTasks.map((task) => (
              <div key={task.taskNumber}>
                <ChallengeTranslationDetail
                  attributeName={`task.${task.id}`}
                  title={{
                    label: t(i18nKeys.myJams.challenges.details.task.title) + ' ' + task.taskNumber,
                    value: task.title || '',
                  }}
                  originalTitle={{
                    label: t(i18nKeys.challenges.challengeDetails.titles.title),
                    value:
                      translatedChallenge?.original?.props?.tasks.find((ta) => ta.taskNumber === task.taskNumber)
                        ?.title || '',
                  }}
                  description={{
                    label: t(i18nKeys.challenges.challengeDetails.titles.content),
                    value: task.content || '',
                  }}
                  originalDescription={{
                    label: t(i18nKeys.challenges.challengeDetails.titles.content),
                    value:
                      translatedChallenge?.original?.props?.tasks.find((ta) => ta.taskNumber === task.taskNumber)
                        ?.content || '',
                  }}
                  formUpdate={({ attributeName, translatedText }) => {
                    const currTask = challenge.props.tasks.find((ta) => ta.id === task.id);
                    const originalText = (attributeName.includes('title') ? currTask?.title : currTask?.content) || '';
                    formUpdate({ attributeName, originalText, translatedText });
                  }}
                  editor
                />
                {!!task?.clues?.length &&
                  task.clues.map((clue, clueIndex) => (
                    <Box key={`task.${task.id}.clue.${clueIndex + 1}`} margin={{ top: 's' }}>
                      <ChallengeTranslationDetail
                        attributeName={`task.${task.id}.clue.${clueIndex + 1}`}
                        title={{
                          label: 'Task ' + task.taskNumber + ': Clue ' + (clueIndex + 1),
                          value: clue.title || '',
                        }}
                        originalTitle={{
                          label: 'Clue' + (clueIndex + 1),
                          value:
                            translatedChallenge?.original?.props?.tasks?.find((ta) => ta.taskNumber === task.taskNumber)
                              ?.clues?.[clueIndex]?.title || '',
                        }}
                        description={{ label: 'Description', value: clue.description || '' }}
                        originalDescription={{
                          label: 'Description',
                          value:
                            translatedChallenge?.original?.props?.tasks?.find((ta) => ta.taskNumber === task.taskNumber)
                              ?.clues?.[clueIndex]?.description || '',
                        }}
                        formUpdate={({ attributeName, translatedText }) => {
                          const currClue = challenge.props.tasks
                            .find((ta) => ta.id === task.id)
                            ?.clues.find((cl) => cl.order === clueIndex + 1);
                          const originalText =
                            (attributeName.includes('title') ? currClue?.title : currClue?.description) || '';
                          formUpdate({ attributeName, originalText, translatedText });
                        }}
                        editor
                      />
                    </Box>
                  ))}
              </div>
            ))}
          </>
        )}
      </SpaceBetween>
    </div>
  );
};

export default ChallengeTranslationDetails;
